import { useId, useState, type CSSProperties, type FormEvent } from 'react';
import type { CourseStandard } from '../../core/course';
import type { PlanEdit } from '../../core/plan-edits';
import { MAX_UNIT_NAME, MAX_UNIT_WEEKS } from '../../core/plan-edits';
import type { ScheduledUnit } from '../../core/schedule';
import type { PlanUnit } from '../../core/types';
import type { UnitTopic } from '../../core/topics';
import type { GetToken } from '../api';
import { unitColor } from '../unit-color';
import { MoveMenu, type MoveTarget } from './MoveMenu';
import { TopicList } from './TopicList';

/** An edit as the browser sends it: a new unit's id is always the Worker's. */
export type EditRequest = Exclude<PlanEdit, { type: 'add_unit' }> | { type: 'add_unit'; name?: string };

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
export const day = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
export const dateRange = (unit: Pick<ScheduledUnit, 'start' | 'end'>) => (unit.start && unit.end ? `${day(unit.start)} – ${day(unit.end)}` : 'No weeks');

/** Where a standard can go from where it is: another unit, Not yet placed, or Not in my course. */
export function moveTargets(standardId: string, from: string | 'not_yet_placed', units: PlanUnit[], edit: (e: EditRequest) => void): MoveTarget[] {
  return [
    ...units.filter(u => u.id !== from).map(u => ({ key: u.id, label: u.name, onSelect: () => edit({ type: 'move_standard', standardId, to: { place: 'unit', unitId: u.id } }) })),
    ...(from === 'not_yet_placed' ? [] : [{ key: '_nyp', label: 'Not yet placed', onSelect: () => edit({ type: 'move_standard', standardId, to: { place: 'not_yet_placed' } }) }]),
    { key: '_nic', label: 'Not in my course', onSelect: () => edit({ type: 'exclude_standard', standardId }) },
  ];
}

export function StandardItem({ standard, children }: { standard: CourseStandard; children?: React.ReactNode }) {
  return <li className="plan-standard"><span><strong className="standard-code">{standard.code || 'Standard'}</strong> <span>{standard.statement}</span></span>{children}</li>;
}

/** A unit's dates, weeks, badges, and overrun flag. */
export function UnitMeta({ unit, current }: { unit: ScheduledUnit; current: boolean }) {
  return <>
    <p className="unit-dates"><span>{dateRange(unit)}</span> · {plural(unit.weeks, 'week')}
      {current && <span className="badge">Current unit</span>}
      {unit.done && <span className="badge done-badge">Done</span>}
    </p>
    {unit.pastLastDay && <p className="unit-flag">Runs {plural(unit.weeksOver, 'week')} past the last day</p>}
  </>;
}

/** One unit: dates from the schedule, its controls, its standards, and its 240 topics (R10 to R12). */
export function UnitRow({ planId, unit, index, count, current, selected = false, units, standards, getToken, onEdit, onOpenTopic }: {
  planId: string; unit: ScheduledUnit; index: number; count: number; current: boolean; selected?: boolean; units: PlanUnit[];
  standards: CourseStandard[]; getToken: GetToken; onEdit(edit: EditRequest): void; onOpenTopic(topic: UnitTopic, unitId: string): void;
}) {
  const titleId = useId();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(unit.name);
  const [topicsOpen, setTopicsOpen] = useState(false);
  const minimum = unit.done ? 0 : 1;
  const rename = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim() && name.trim() !== unit.name) onEdit({ type: 'rename_unit', unitId: unit.id, name });
    setRenaming(false);
  };
  return <section className={`unit-row${unit.done ? ' done' : ''}${current ? ' current' : ''}${unit.pastLastDay ? ' over' : ''}${selected ? ' selected' : ''}`}
    aria-labelledby={titleId} data-unit-id={unit.id} style={{ '--unit-color': unitColor(index) } as CSSProperties}>
    <div className="unit-top">
      <div className="unit-title">
        <span className="unit-number">Unit {index + 1}</span>
        {renaming
          ? <form className="rename-form" onSubmit={rename}>
              <label className="sr-only" htmlFor={`${titleId}-name`}>Unit name</label>
              <input id={`${titleId}-name`} type="text" maxLength={MAX_UNIT_NAME} value={name} autoFocus onChange={e => setName(e.target.value)} />
              <button className="secondary" type="submit">Save</button>
              <button className="text-button" type="button" onClick={() => { setName(unit.name); setRenaming(false); }}>Cancel</button>
            </form>
          : null}
        <h2 id={titleId} className={renaming ? 'sr-only' : undefined}>{unit.name}</h2>
        <UnitMeta unit={unit} current={current} />
      </div>
      <div className="unit-controls">
        <div className="stepper" role="group" aria-label={`Weeks for ${unit.name}`}>
          <button type="button" aria-label={`Fewer weeks for ${unit.name}`} disabled={unit.weeks <= minimum} onClick={() => onEdit({ type: 'set_weeks', unitId: unit.id, weeks: unit.weeks - 1 })}>−</button>
          <span aria-hidden="true">{unit.weeks}</span>
          <button type="button" aria-label={`More weeks for ${unit.name}`} disabled={unit.weeks >= MAX_UNIT_WEEKS} onClick={() => onEdit({ type: 'set_weeks', unitId: unit.id, weeks: unit.weeks + 1 })}>+</button>
        </div>
        <button type="button" className="icon-button" aria-label={`Move ${unit.name} up`} disabled={index === 0} onClick={() => onEdit({ type: 'move_unit', unitId: unit.id, direction: 'up' })}>↑</button>
        <button type="button" className="icon-button" aria-label={`Move ${unit.name} down`} disabled={index === count - 1} onClick={() => onEdit({ type: 'move_unit', unitId: unit.id, direction: 'down' })}>↓</button>
        <button type="button" className="text-button" aria-label={`Rename ${unit.name}`} onClick={() => { setName(unit.name); setRenaming(true); }}>Rename</button>
        <button type="button" className="text-button danger" aria-label={`Delete ${unit.name}`} onClick={() => onEdit({ type: 'delete_unit', unitId: unit.id })}>Delete</button>
      </div>
    </div>
    {standards.length
      ? <ul className="plan-standards">{standards.map(s => <StandardItem key={s.id} standard={s}>
          <MoveMenu label={`Move ${s.code || s.statement} to`} targets={moveTargets(s.id, unit.id, units, onEdit)} />
        </StandardItem>)}</ul>
      : <p className="muted unit-empty">No standards in this unit yet. Move some here from Not yet placed.</p>}
    {standards.length > 0 && <div className="unit-topics">
      <button type="button" className="text-button" aria-expanded={topicsOpen} onClick={() => setTopicsOpen(o => !o)}>{topicsOpen ? 'Hide 240 topics' : 'Show 240 topics'}</button>
      {topicsOpen && <TopicList planId={planId} unitId={unit.id} standardIds={standards.map(s => s.id)} getToken={getToken} onOpen={topic => onOpenTopic(topic, unit.id)} />}
    </div>}
  </section>;
}
